import React, { useEffect, useMemo, useState } from 'react';

// Time slots:
//  sunrise  5:00 -  8:00
//  morning  8:00 - 12:00
//  afternoon 12:00 - 17:00
//  dusk     17:00 - 20:00
//  night    20:00 -  5:00 (default fallback)
const SLOTS = [
  { key: 'sunrise',  start: 5,  end: 8,  label: 'Good morning',  accent: 'rgba(255, 188, 130, 0.18)' },
  { key: 'morning',  start: 8,  end: 12, label: 'Good morning',  accent: 'rgba(255, 209, 144, 0.16)' },
  { key: 'afternoon',start: 12, end: 17, label: 'Good afternoon',accent: 'rgba(246, 178, 74, 0.18)' },
  { key: 'dusk',     start: 17, end: 20, label: 'Good evening',  accent: 'rgba(220, 110, 200, 0.18)' },
  { key: 'night',    start: 20, end: 5,  label: 'Good evening', accent: 'rgba(45, 226, 230, 0.10)' },
];

export function getCurrentSlot(date = new Date()) {
  // Allow URL override: ?bg=sunrise|morning|afternoon|dusk|night
  try {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search).get('bg');
      if (p) {
        const override = SLOTS.find((s) => s.key === p);
        if (override) return override;
      }
    }
  } catch (_) { /* noop */ }
  const h = date.getHours();
  for (const s of SLOTS) {
    if (s.key === 'night') {
      if (h >= 20 || h < 5) return s;
    } else if (h >= s.start && h < s.end) {
      return s;
    }
  }
  return SLOTS[SLOTS.length - 1];
}

/**
 * Full-screen, fixed, behind-content adaptive background that shifts with
 * the local time of day. Crossfades smoothly between slots (~1500ms).
 *
 * Render at the root of <App /> once; positioned `fixed` so it stays put
 * while pages scroll.
 */
export function TimeOfDayBackground({ intensity = 0.32 }) {
  const [slot, setSlot] = useState(() => getCurrentSlot());
  const [prevSlot, setPrevSlot] = useState(null);

  useEffect(() => {
    const tick = () => {
      const next = getCurrentSlot();
      setSlot((current) => {
        if (current.key !== next.key) {
          setPrevSlot(current);
          // Clear prev slot after fade duration
          setTimeout(() => setPrevSlot(null), 1800);
          return next;
        }
        return current;
      });
    };
    // Recheck every 60 seconds
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const bgUrl = useMemo(() => `url(/bg-${slot.key}.jpg)`, [slot.key]);
  const prevUrl = useMemo(() => (prevSlot ? `url(/bg-${prevSlot.key}.jpg)` : null), [prevSlot]);

  return (
    <div
      aria-hidden
      data-testid="time-of-day-background"
      data-slot={slot.key}
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
    >
      {/* Previous slot fading out */}
      {prevUrl && (
        <div
          className="absolute inset-0 transition-opacity duration-[1600ms]"
          style={{
            backgroundImage: prevUrl,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0,
            filter: 'blur(0.5px) saturate(1.05)',
          }}
        />
      )}
      {/* Current slot fading in */}
      <div
        className="absolute inset-0 transition-opacity duration-[1600ms]"
        style={{
          backgroundImage: bgUrl,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: intensity,
          filter: 'saturate(1.08)',
        }}
      />
      {/* Color accent wash to enhance time-of-day mood */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(900px circle at 25% 12%, ${slot.accent}, transparent 60%),
                       radial-gradient(700px circle at 80% 88%, ${slot.accent}, transparent 60%)`,
        }}
      />
      {/* Minimal readability vignette: very light at top, slight at bottom */}
      <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-[#07070A]/55 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#07070A]/85 to-transparent" />
    </div>
  );
}

export { SLOTS as TIME_SLOTS };
